if (!customElements.get('collection-load-more')) {
  customElements.define(
    'collection-load-more',
    class CollectionLoadMore extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.mode = this.dataset.mode || 'button';
        this.sectionId = this.dataset.sectionId;
        this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
        this.button = this.querySelector('[data-load-more-button]');
        this.sentinel = this.querySelector('[data-load-more-sentinel]');
        this.loading = false;

        this.button?.addEventListener('click', () => this.load());

        if (this.mode === 'infinite' && this.dataset.nextUrl && this.sentinel) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.load();
            },
            { rootMargin: '0px 0px 500px 0px' }
          );
          this.observer.observe(this.sentinel);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        const nextUrl = this.dataset.nextUrl;
        if (!nextUrl || this.loading || !this.grid) return;

        this.setLoading(true);
        let failed = false;

        try {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('Failed to load more products');

          const html = await response.text();
          if (!this.isConnected) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newItems = doc.querySelectorAll('#product-grid > li');
          if (!newItems.length) {
            this.dataset.nextUrl = '';
            this.finish();
            return;
          }

          newItems.forEach((item) => {
            item.classList.add('scroll-trigger--cancel');
            item.querySelectorAll('.scroll-trigger').forEach((element) => {
              element.classList.add('scroll-trigger--cancel');
            });
            this.grid.appendChild(item);
          });

          const incoming = doc.querySelector('collection-load-more');
          const incomingNext = incoming?.dataset.nextUrl || '';
          this.dataset.nextUrl = incomingNext === nextUrl ? '' : incomingNext;

          const shown = this.grid.querySelectorAll(':scope > li').length;
          const total = Number(incoming?.dataset.total || this.dataset.total || shown);
          this.updateProgress(shown, total);

          if (!this.dataset.nextUrl) this.finish();
        } catch (error) {
          failed = true;
        } finally {
          if (this.isConnected) this.setLoading(false);
          if (
            !failed &&
            this.isConnected &&
            this.mode === 'infinite' &&
            this.dataset.nextUrl &&
            this.sentinelNearViewport()
          ) {
            this.load();
          }
        }
      }

      updateProgress(shown, total) {
        const shownCount = Number(shown) || 0;
        const totalCount = Number(total) || 0;
        const percent = totalCount > 0 ? Math.min(100, (shownCount / totalCount) * 100) : 0;

        this.dataset.shown = String(shownCount);
        this.dataset.total = String(totalCount);

        const shownEl = this.querySelector('[data-load-more-shown]');
        const totalEl = this.querySelector('[data-load-more-total]');
        if (shownEl) shownEl.textContent = String(shownCount);
        if (totalEl) totalEl.textContent = String(totalCount);

        const bar = this.querySelector('[data-load-more-bar]');
        if (bar) bar.style.width = `${percent}%`;

        const track = this.querySelector('[data-load-more-track]');
        if (track) {
          track.setAttribute('aria-valuenow', String(shownCount));
          track.setAttribute('aria-valuemax', String(totalCount));
          track.setAttribute('aria-label', `Showing ${shownCount} of ${totalCount} products`);
        }
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        if (this.button) {
          this.button.disabled = isLoading;
          this.button.classList.toggle('is-loading', isLoading);
          this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        }
        this.querySelector('[data-load-more-spinner]')?.classList.toggle('hidden', !isLoading);
      }

      finish() {
        this.observer?.disconnect();
        this.button?.remove();
        this.sentinel?.remove();
        this.querySelector('[data-load-more-spinner]')?.remove();
      }

      sentinelNearViewport() {
        if (!this.sentinel) return false;
        const rect = this.sentinel.getBoundingClientRect();
        return rect.top <= window.innerHeight + 500;
      }
    }
  );
}
